import React, { useState, useEffect, useRef } from 'react';
import CommonModal from '../../../extras/commonModal';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Chip,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import {
  updateScrapeRequest,
  getAllKeywords,
} from '../../../services/apiService';
import LoadingCircleIcon from '../../../icons/LoadingCircle';

const UpdateEditRequest = ({
  openEditModal,
  handleCloseEditModal,
  selectedRowForEdit,
  handleUpdateSuccess,
  gridApi,
  refetchData,
}) => {
  console.log(selectedRowForEdit, 'request');

  // Keyword-related states (same as CreateRequest)
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    maxHeight: 200,
  });
  const debounceTimeoutRef = useRef(null);
  const keywordInputRef = useRef(null);

  const [formValues, setFormValues] = useState({
    id: '',
    requestName: '',
    platform: '',
    type: '',
    frequency: '',
    keywords: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Calculate dropdown position and max height (same as CreateRequest)
  const calculateDropdownPosition = () => {
    if (keywordInputRef.current) {
      const inputRect = keywordInputRef.current.getBoundingClientRect();
      const modalContent = keywordInputRef.current.closest('.overflow-y-auto');
      const modalRect = modalContent
        ? modalContent.getBoundingClientRect()
        : { bottom: window.innerHeight, top: 0 };

      const spaceBelow = modalRect.bottom - inputRect.bottom - 40; // 40px padding for safety
      const spaceAbove = inputRect.top - modalRect.top - 40;

      // Always prefer showing below unless there's significantly more space above
      const shouldOpenUpward = spaceBelow < 150 && spaceAbove > spaceBelow + 50;

      setDropdownPosition({
        maxHeight: Math.min(
          shouldOpenUpward
            ? Math.max(spaceAbove, 200)
            : Math.max(spaceBelow, 200),
          300
        ),
        openUpward: shouldOpenUpward,
      });
    }
  };

  // Debounced fetch function (same as CreateRequest)
  const debouncedFetchKeywords = (searchTerm) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      fetchKeywords(searchTerm);
    }, 300); // 300ms delay
  };

  // Fetch keywords from API (same as CreateRequest)
  const fetchKeywords = async (searchTerm = '') => {
    setKeywordLoading(true);
    try {
      const response = await getAllKeywords();

      // Map the API response to the format needed by the component
      let mappedKeywords = [];

      if (response && response.data && Array.isArray(response.data)) {
        mappedKeywords = response.data.map((item) => ({
          id: item.id,
          name: item.keyword,
          value: item.keyword.toLowerCase().replace(/\s+/g, '-'),
          platform: item?.platform,
        }));
      }

      // Filter by search term if provided
      if (searchTerm) {
        mappedKeywords = mappedKeywords.filter((keyword) =>
          keyword.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setKeywordOptions(mappedKeywords);
    } catch (error) {
      console.error('Failed to fetch keywords:', error);
      toast.error('Failed to load keywords');
      setKeywordOptions([]); // Set empty array on error
    } finally {
      setKeywordLoading(false);
    }
  };

  // Load initial keywords when component mounts (same as CreateRequest)
  useEffect(() => {
    if (openEditModal) {
      fetchKeywords();
    }

    // Cleanup debounce timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [openEditModal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle keyword selection/addition (same as CreateRequest)
  const handleAddKeyword = (keyword) => {
    if (!selectedKeywords.find((k) => k.id === keyword.id)) {
      setSelectedKeywords([...selectedKeywords, keyword]);
      setKeywordSearch('');
      setShowSuggestions(false);
    }
  };

  // Handle adding new keyword (same as CreateRequest)
  const handleAddNewKeyword = () => {
    if (
      keywordSearch.trim() &&
      !selectedKeywords.find(
        (k) => k.name.toLowerCase() === keywordSearch.toLowerCase()
      )
    ) {
      const newKeyword = {
        id: `new-${Date.now()}`,
        name: keywordSearch.trim(),
        value: keywordSearch.toLowerCase().replace(/\s+/g, '-'),
        isNew: true,
      };
      setSelectedKeywords([...selectedKeywords, newKeyword]);
      setKeywordSearch('');
      setShowSuggestions(false);
    }
  };

  // Handle removing keyword (same as CreateRequest)
  const handleRemoveKeyword = (keywordToRemove) => {
    setSelectedKeywords(
      selectedKeywords.filter((k) => k.id !== keywordToRemove.id)
    );
  };

  // Handle keyword search input (same as CreateRequest)
  const handleKeywordSearchChange = (e) => {
    const value = e.target.value;
    setKeywordSearch(value);
    setShowSuggestions(value.length > 0);

    if (value.length > 0) {
      // Use debounced fetch instead of direct fetch
      debouncedFetchKeywords(value);
      // Calculate position when showing suggestions
      setTimeout(calculateDropdownPosition, 0);
    } else {
      // Clear timeout if search is empty
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    }
  };

  // Handle input focus (same as CreateRequest)
  const handleKeywordInputFocus = () => {
    if (keywordSearch.length > 0) {
      setShowSuggestions(true);
      calculateDropdownPosition();
    }
  };

  // Handle input blur with delay to allow clicks on dropdown (same as CreateRequest)
  const handleKeywordInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};

    // Check required fields
    if (!formValues.platform) newErrors.platform = 'Platform is required';
    if (!formValues.type) newErrors.type = 'Type is required';

    // Frequency validation for Cron type
    if (formValues.type === 'Cron' && !formValues.frequency) {
      newErrors.frequency = 'Frequency is required for Cron type';
    }

    // Keywords validation
    if (!selectedKeywords || selectedKeywords.length === 0) {
      newErrors.keywords = 'At least one keyword is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async (updateData) => {
    
    try {
      setIsLoading(true);
      const response = await updateScrapeRequest(updateData);
      toast.success('Request updated successfully');
      return response;
    } catch (error) {
      toast.error('Failed to update request');
      console.error(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

const handleUpdateRequest = async () => {
  if (!validateForm()) return;
  console.log(selectedKeywords, 'keywords');
  
  // Prepare keyword data - handle different keyword types properly
  const requestIds = [];
  
  selectedKeywords.forEach((keyword) => {
    if (keyword.isExisting) {
      // For existing keywords from the row data, use the keyword name
      requestIds.push(keyword.name);
    } else if (keyword.isNew) {
      // For new keywords, use the keyword name as string
      requestIds.push(keyword.name);
    } else {
      // For keywords selected from API (have actual numeric IDs), use the actual ID
      requestIds.push(keyword.id);
    }
  });

  const updateData = {
    id: formValues.id,
    requestName: formValues.requestName,
    requestIds: requestIds, // Contains proper keyword names/IDs without temporary IDs
    platform: formValues.platform,
    type: formValues.type,
    frequency: formValues.type === 'Cron' ? formValues.frequency : 'one-time',
  };

  console.log('Update request payload:', updateData);

  const response = await handleUpdate(updateData);

  if (response) {
    handleCloseEditModal();
    if (gridApi) {
      refetchData();
    }
    if (handleUpdateSuccess) {
      handleUpdateSuccess();
    }
  }
};
  const handleClose = () => {
    handleCloseEditModal();
    setErrors({});
    setSelectedKeywords([]);
    setKeywordSearch('');
    setShowSuggestions(false);

    // Clear any pending debounced requests
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  };

  console.log(formValues, 'formvaluesh');

  useEffect(() => {
    if (selectedRowForEdit && openEditModal) {
      setFormValues({
        requestName: selectedRowForEdit.requestName || '',
        id: selectedRowForEdit.id || '',
        platform: selectedRowForEdit.platform || '',
        type: selectedRowForEdit.type || '',
        frequency: selectedRowForEdit.frequency || '',
        keywords: selectedRowForEdit.keyword || '',
      });

      // Handle existing keywords - convert to the new format
      const keywordsData =
        selectedRowForEdit.keyword || selectedRowForEdit.keywords || '';
      let keywordArray = [];

      if (keywordsData) {
        if (typeof keywordsData === 'string') {
          // Split by comma and create keyword objects
          const keywordNames = keywordsData
            .split(',')
            .map((k) => k.trim())
            .filter((k) => k);
          keywordArray = keywordNames.map((name, index) => ({
            id: `existing-${selectedRowForEdit.id}-${index}`,
            name: name,
            value: name.toLowerCase().replace(/\s+/g, '-'),
            isExisting: true, // Flag to identify existing keywords from the row
          }));
        } else if (Array.isArray(keywordsData)) {
          keywordArray = keywordsData.map((keyword, index) => ({
            id: `existing-${selectedRowForEdit.id}`,
            name: keyword.toString().trim(),
            value: keyword.toString().toLowerCase().replace(/\s+/g, '-'),
            isExisting: true,
          }));
        }
      }

      // Set the selected keywords
      setSelectedKeywords(keywordArray);

      // Clear any previous errors
      setErrors({});
    }
  }, [selectedRowForEdit, openEditModal]);

  return (
    <CommonModal
      isOpen={openEditModal}
      setIsOpen={handleClose}
      removePadding
      classStyle='w-[70%] h-auto max-h-[85vh] rounded-md overflow-hidden'
    >
      <div className='flex flex-col h-full max-h-[85vh]'>
        {/* Header */}
        <div className='p-6 border-b border-gray-200 flex-shrink-0'>
          <h2 className='text-xl font-semibold'>Edit Request</h2>
        </div>

        {/* Scrollable Content */}
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <TextField
              label='Request Name'
              name='requestName'
              variant='outlined'
              fullWidth
              value={formValues.requestName}
              onChange={handleChange}
              error={!!errors.requestName}
            />

            {/* Input ID - Disabled as requested */}
            <TextField
              label='Input ID'
              name='inputId'
              variant='outlined'
              fullWidth
              value={formValues.id}
              disabled
            />

            {/* Platform Selection */}
            <FormControl variant='outlined' fullWidth error={!!errors.platform}>
              <InputLabel id='platform-label'>Platform</InputLabel>
              <Select
                labelId='platform-label'
                name='platform'
                value={formValues.platform}
                onChange={handleChange}
                label='Platform'
                disabled
              >
                <MenuItem value='LinkedIn'>LinkedIn</MenuItem>
                <MenuItem value='Naukri'>Naukri</MenuItem>
              </Select>
              {errors.platform && (
                <p className='text-red-500 text-xs mt-1'>{errors.platform}</p>
              )}
            </FormControl>

            {/* Type Selection */}
            <FormControl variant='outlined' fullWidth error={!!errors.type}>
              <InputLabel id='type-label'>Type</InputLabel>
              <Select
                labelId='type-label'
                name='type'
                value={formValues.type}
                onChange={handleChange}
                label='Type'
              >
                <MenuItem value='Cron'>Cron</MenuItem>
                <MenuItem value='OneTime'>One Time</MenuItem>
              </Select>
              {errors.type && (
                <p className='text-red-500 text-xs mt-1'>{errors.type}</p>
              )}
            </FormControl>

            {/* Frequency - only shown for Cron type */}
            {formValues.type === 'Cron' && (
              <TextField
                label='Frequency (in hours)'
                name='frequency'
                variant='outlined'
                fullWidth
                type='text'
                value={formValues.frequency}
                onChange={(e) => {
                  const value = e.target.value;

                  // Show error if pasted/typed invalid value
                  if (value === '' || /^[0-9]*$/.test(value)) {
                    setFormValues((prev) => ({
                      ...prev,
                      frequency: value,
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      frequency: '',
                    }));
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      frequency: 'Only positive integers are allowed',
                    }));
                  }
                }}
                onKeyDown={(e) => {
                  const allowedKeys = [
                    'Backspace',
                    'ArrowLeft',
                    'ArrowRight',
                    'Tab',
                    'Delete',
                  ];

                  if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                    setErrors((prev) => ({
                      ...prev,
                      frequency: 'Only Integer Values are allowed',
                    }));
                  }
                }}
                error={!!errors.frequency}
                helperText={
                  errors.frequency || 'How often to run this request (in hours)'
                }
              />
            )}

            <div className='col-span-2'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Keywords
                <span className='text-sm text-gray-500 ml-2'>
                  (Search and select existing keywords or add new ones)
                </span>
              </label>

              {selectedKeywords?.length > 0 && (
                <div className='mb-3 flex flex-wrap gap-2'>
                  {selectedKeywords?.map((keyword) => (
                    <Chip
                      key={keyword.id}
                      label={keyword.name}
                      onDelete={() => handleRemoveKeyword(keyword)}
                      deleteIcon={<CloseIcon />}
                      color={
                        keyword.isNew
                          ? 'secondary'
                          : keyword.isExisting
                          ? 'default'
                          : 'primary'
                      }
                      variant='outlined'
                      size='small'
                    />
                  ))}
                </div>
              )}

              <div className='relative' style={{ zIndex: 20 }}>
                <TextField
                  ref={keywordInputRef}
                  fullWidth
                  variant='outlined'
                  placeholder='Search keywords or type to add new ones...'
                  value={keywordSearch}
                  onChange={handleKeywordSearchChange}
                  onFocus={handleKeywordInputFocus}
                  onBlur={handleKeywordInputBlur}
                  error={!!errors.keywords}
                  helperText={errors.keywords}
                  InputProps={{
                    endAdornment: keywordLoading ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null,
                  }}
                />

                {/* Improved Suggestions Dropdown */}
                {showSuggestions && (
                  <Paper
                    className='absolute z-10 w-full mt-1 max-h-60 overflow-y-auto'
                    //   top: dropdownPosition.openUpward ? 'auto' : '100%',
                    //   bottom: dropdownPosition.openUpward ? '100%' : 'auto',
                    //   marginTop: dropdownPosition.openUpward ? 0 : 8,
                    //   marginBottom: dropdownPosition.openUpward ? 8 : 0,
                    //   maxHeight: dropdownPosition.maxHeight,
                    //   zIndex: 1000,
                    //   boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    // }}
                    elevation={3}
                  >
                    <div
                      className='overflow-y-auto'
                      style={{ maxHeight: dropdownPosition.maxHeight - 8 }}
                    >
                      <List dense disablePadding>
                        {/* Add new keyword option */}
                        {keywordSearch.trim() &&
                          !keywordOptions.find(
                            (k) =>
                              k.name.toLowerCase() ===
                              keywordSearch.toLowerCase()
                          ) &&
                          !selectedKeywords.find(
                            (k) =>
                              k.name.toLowerCase() ===
                              keywordSearch.toLowerCase()
                          ) && (
                            <ListItem
                              button
                              onClick={handleAddNewKeyword}
                              className='border-b border-gray-200 bg-green-50 hover:bg-green-100 transition-colors'
                              style={{ padding: '8px 16px' }}
                            >
                              <AddIcon
                                className='mr-2 text-green-600'
                                style={{ fontSize: 20 }}
                              />
                              <ListItemText
                                primary={`Add "${keywordSearch}"`}
                                secondary='Create new keyword'
                                primaryTypographyProps={{
                                  style: { fontSize: '14px', fontWeight: 500 },
                                }}
                                secondaryTypographyProps={{
                                  style: { fontSize: '12px' },
                                }}
                              />
                            </ListItem>
                          )}

                        {/* Existing keyword options */}
                        {keywordOptions
                          .filter(
                            (option) =>
                              !selectedKeywords.find((k) => k.id === option.id)
                          )
                          .slice(0, 10) // Limit to 10 items for better performance
                          .map((option) => (
                            <ListItem
                              button
                              key={option.id}
                              onClick={() => handleAddKeyword(option)}
                              className='hover:bg-gray-50 transition-colors'
                              style={{ padding: '8px 16px' }}
                            >
                              <ListItemText
                                primary={option?.name}
                                secondary={`Platform: ${option?.platform}`}
                                primaryTypographyProps={{
                                  style: { fontSize: '14px' },
                                }}
                                secondaryTypographyProps={{
                                  style: { fontSize: '12px' },
                                }}
                              />
                            </ListItem>
                          ))}

                        {/* Show more indicator if there are more results */}
                        {keywordOptions.filter(
                          (option) =>
                            !selectedKeywords.find((k) => k.id === option.id)
                        ).length > 10 && (
                          <ListItem
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#f9fafb',
                            }}
                          >
                            <ListItemText
                              primary={`And ${
                                keywordOptions.filter(
                                  (option) =>
                                    !selectedKeywords.find(
                                      (k) => k.id === option.id
                                    )
                                ).length - 10
                              } more...`}
                              primaryTypographyProps={{
                                style: {
                                  fontSize: '12px',
                                  fontStyle: 'italic',
                                  color: '#6b7280',
                                },
                              }}
                            />
                          </ListItem>
                        )}

                        {keywordOptions.length === 0 &&
                          !keywordLoading &&
                          keywordSearch.length > 0 && (
                            <ListItem style={{ padding: '8px 16px' }}>
                              <ListItemText
                                primary='No keywords found'
                                primaryTypographyProps={{
                                  style: { fontSize: '14px', color: '#6b7280' },
                                }}
                              />
                            </ListItem>
                          )}
                      </List>
                    </div>
                  </Paper>
                )}
              </div>

              {selectedKeywords.length > 0 && (
                <div className='mt-2'>
                  <p className='text-xs text-gray-500'>
                    Selected: {selectedKeywords.length} keyword(s)
                    {selectedKeywords.filter((k) => k.isNew).length > 0 && (
                      <span className='text-orange-600 ml-2'>
                        ({selectedKeywords.filter((k) => k.isNew).length} new)
                      </span>
                    )}
                    {selectedKeywords.filter((k) => k.isExisting).length >
                      0 && (
                      <span className='text-blue-600 ml-2'>
                        ({selectedKeywords.filter((k) => k.isExisting).length}{' '}
                        existing)
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Action Buttons */}
        <div className='flex justify-end space-x-4 p-4 bg-gray-50 border-t border-gray-200 flex-shrink-0'>
          <button
            onClick={handleClose}
            className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors'
          >
            Cancel
          </button>

          <button
            onClick={handleUpdateRequest}
            disabled={isLoading}
            className={`px-4 py-2 rounded-md transition-colors flex items-center ${
              isLoading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-brand-primary text-white hover:bg-opacity-90'
            }`}
          >
            {isLoading ? (
              <>
                <LoadingCircleIcon iconStyle='w-5 mr-2 animate-spin' />
                Updating...
              </>
            ) : (
              'Update Request'
            )}
          </button>
        </div>
      </div>
    </CommonModal>
  );
};

export default UpdateEditRequest;